/* ----------------------------------------------------------------------------
   M0 · Courier Partner Directory  (route /supply/partners)
   React port of v6 renderPartners() (mockup lines 2053-2393): page-head with
   "Update Rates & TAT" + "Onboard Partner" actions, SupplyTable over the D2C
   parents (partners with ≥1 D2C account code) with avatar / # Account Codes /
   Active (colored x/y) / Delivery Modes / Agreements / Onboarding / Status /
   Last Updated columns, status filter, "All time" date-range chip (dateColumn
   'updated'), expandable per-code sub-table (service-type chips, delivery-mode
   chips, # agreements pill, onboarded by, status pill, Commercials · Rates &
   TAT · Parent 360 links + eye deep-dive), row actions (View 360 / Edit
   details / Activate-Deactivate with confirm), bulk Activate / Deactivate
   (reason capture) / Export selected (CSV), empty state with Onboard CTA.
   ---------------------------------------------------------------------------- */
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined'
import PowerSettingsNewOutlinedIcon from '@mui/icons-material/PowerSettingsNewOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined'
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined'
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined'
import {Box, Stack, Typography, Chip, Button} from '@mui/material'
import {alpha} from '@mui/material/styles'
import PageTitle from '../../../components/PageTitle'
import {Badge, SupplyTable, ConfirmDialog, useToast, TableSkeleton, LoadErrorPanel, SupplyHero, HERO_BTN, HERO_BTN_SOLID} from '../components'
import {scoreColor, initials, cx, download, toCsv} from '../components'
import {SERVICE_TYPES_BY_CODE, deliveryModesOf, fmtDate} from '../dummyData'
import {apiError} from '../supplyMeta'
import {
  useListParentsQuery,
  useUpdateParentStatusMutation,
  useLazyCodesByParentQuery,
  useLazyAgreementTimelineQuery,
} from '../../../redux/supplyApiSlice'
import {mapParent, mapCode, timelineSummary, itemCoversCode} from './mapping'

const ICO = {fontSize: 16}

/* delivery-mode labels across a parent's loaded codes */
const modesForCodes = (codes) => {
  const allST = []
  ;(codes || []).forEach((c) => (c.serviceTypes || []).forEach((st) => allST.push(st)))
  return deliveryModesOf(allST)
}

/* Count ACTIVE agreements (timeline items) that cover a specific code. */
const agrCountForCode = (timeline, code) =>
  (timeline || []).filter((t) => (t.agreement || {}).status === 'ACTIVE' && itemCoversCode(t, code)).length

/* ── date-range chip (port of the dataTable dateColumn engine, TRD 2.2.2) ─── */
const dayStart = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0)
const dayEnd = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999)

/* relative "x days ago" against the real clock (dummy relLabel pins TODAY) */
const relNow = (d) => {
  if (!d) return '—'
  const days = Math.round((Date.now() - new Date(d).getTime()) / 86400000)
  if (days <= 0) return 'today'
  if (days === 1) return '1 day ago'
  if (days < 30) return days + ' days ago'
  if (days < 60) return '1 mo ago'
  return Math.round(days / 30) + ' mo ago'
}

function presetRange(preset, from, to) {
  const T = new Date()
  if (preset === 'today') return {lo: dayStart(T), hi: dayEnd(T)}
  if (preset === '7d') return {lo: dayStart(new Date(T.getTime() - 6 * 86400000)), hi: dayEnd(T)}
  if (preset === '30d') return {lo: dayStart(new Date(T.getTime() - 29 * 86400000)), hi: dayEnd(T)}
  if (preset === 'month') return {lo: dayStart(new Date(T.getFullYear(), T.getMonth(), 1)), hi: dayEnd(T)}
  if (preset === 'custom')
    return {lo: from ? dayStart(new Date(from)) : null, hi: to ? dayEnd(new Date(to)) : dayEnd(T)}
  return null
}

function drLabel(range) {
  if (!range || range.preset === 'all') return 'All time'
  const r = presetRange(range.preset, range.from, range.to)
  if (!r || !r.hi) return 'All time'
  if (range.preset === 'today') return fmtDate(r.hi)
  if (!r.lo) return '… – ' + fmtDate(r.hi)
  let lo = fmtDate(r.lo)
  const hi = fmtDate(r.hi)
  if (r.lo.getFullYear() === r.hi.getFullYear()) lo = lo.replace(/\s*\d{4}$/, '')
  return lo + ' – ' + hi
}

const DR_PRESETS = [
  ['today', 'Today'],
  ['7d', 'Last 7 days'],
  ['30d', 'Last 30 days'],
  ['month', 'This month'],
  ['custom', 'Custom'],
  ['all', 'All time'],
]

function DateRangeChip({range, onChange}) {
  const [open, setOpen] = useState(false)
  const [picking, setPicking] = useState(false)
  const [from, setFrom] = useState(range.from || '')
  const [to, setTo] = useState(range.to || '')
  const wrapRef = useRef(null)
  const active = range.preset !== 'all'

  useEffect(() => {
    if (!open) return undefined
    const close = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])

  const apply = (preset, f, t) => {
    onChange({preset, from: f || '', to: t || ''})
    setPicking(false)
    setOpen(false)
  }

  return (
    <span ref={wrapRef} style={{position: 'relative', display: 'inline-flex'}}>
      <span
        className={cx('chip', 'chip-date', active && 'on')}
        role='button'
        tabIndex={0}
        onClick={() => setOpen((o) => !o)}
      >
        <span style={{display: 'flex'}}>
          <CalendarTodayOutlinedIcon style={{fontSize: 14}} />
        </span>
        <span>{drLabel(range)}</span>
        {active ? (
          <span
            className='x'
            onClick={(e) => {
              e.stopPropagation()
              apply('all', '', '')
            }}
          >
            ✕
          </span>
        ) : null}
      </span>
      {open ? (
        <div className='dr-pop' style={{top: 38}}>
          <div className='dr-presets'>
            {DR_PRESETS.map(([k, lbl]) => (
              <button
                key={k}
                className={cx(
                  'dr-preset',
                  (picking ? k === 'custom' : range.preset === k) && 'active'
                )}
                onClick={() => {
                  if (k === 'custom') {
                    setPicking(true)
                    return
                  }
                  apply(k, '', '')
                }}
              >
                {lbl}
              </button>
            ))}
          </div>
          {picking || range.preset === 'custom' ? (
            <div className='dr-custom'>
              <input type='date' className='input' value={from} onChange={(e) => setFrom(e.target.value)} />
              <span className='muted'>–</span>
              <input type='date' className='input' value={to} onChange={(e) => setTo(e.target.value)} />
              <button className='btn btn-sm btn-primary' onClick={() => apply('custom', from, to)}>
                Apply
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </span>
  )
}

/* ── Inline sub-table components ─────────────────────────────────────────── */

function ServiceTypeChips({codes, max = 6}) {
  const list = codes || []
  if (!list.length) return <Typography component='span' sx={{fontSize: 12, color: 'text.disabled'}}>—</Typography>
  const shown = list.slice(0, max)
  const rest = list.slice(max)
  return (
    <>
      {shown.map((c) => {
        const st = SERVICE_TYPES_BY_CODE[c]
        const tip = st ? st.name + (st.note ? ' — ' + st.note : '') : c
        return <Chip key={c} size='small' title={tip} label={c} sx={{height: 19, fontSize: 10, fontFamily: "'JetBrains Mono', monospace", bgcolor: 'action.hover', '& .MuiChip-label': {px: 0.75}}} />
      })}
      {rest.length ? <Chip size='small' title={rest.join(', ')} label={'+' + rest.length} sx={{height: 19, fontSize: 10, fontWeight: 700, bgcolor: 'action.hover', '& .MuiChip-label': {px: 0.75}}} /> : null}
    </>
  )
}

/* delivery-mode chips for a list of mode labels */
function ModeChips({modes}) {
  if (!modes || !modes.length) return <Typography component='span' sx={{fontSize: 11, color: 'text.disabled'}}>—</Typography>
  return (
    <>
      {modes.map((m) => (
        <Chip key={m} size='small' label={m} sx={{height: 19, fontSize: 10, bgcolor: 'action.hover', '& .MuiChip-label': {px: 0.75}}} />
      ))}
    </>
  )
}

/* code status pill — Active / Draft / Inactive */
function CodeStatusPill({status}) {
  if (status === 'Active') return <Badge status='active' />
  if (status === 'Draft') return <Chip size='small' label='Draft' sx={{height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: '#FEF3C7', color: '#92400E'}} />
  return <Badge status='inactive' />
}

function CodeSubTable({p, entry, navigate, onExpand}) {
  /* lazy fetch on first expand */
  useEffect(() => {
    onExpand(p.id)
  }, [p.id, onExpand])

  if (!entry || entry.loading) {
    return <Box sx={{p: 2, color: 'text.secondary', fontSize: 13}}>Loading account codes…</Box>
  }
  if (entry.error) {
    return (
      <Box sx={{p: 2, display: 'flex', alignItems: 'center', gap: 1, color: 'error.main', fontSize: 13}}>
        <InfoOutlinedIcon sx={{fontSize: 16}} />
        {entry.error}
      </Box>
    )
  }
  const codes = entry.codes || []
  if (!codes.length) {
    return (
      <Box sx={{p: 2, display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', fontSize: 13}}>
        <InfoOutlinedIcon sx={{fontSize: 16}} />
        No account codes for this partner yet.
      </Box>
    )
  }

  const go = (e, path) => {
    e.preventDefault()
    e.stopPropagation()
    navigate(path)
  }

  return (
    <Box sx={{p: 1.5, bgcolor: (th) => alpha(th.palette.text.primary, 0.015), maxHeight: 380, overflow: 'auto'}}>
      <Stack spacing={1}>
        {codes.map((c) => {
          const agrCnt = agrCountForCode(entry.timeline, c.code)
          const goRates = '/supply/partners/update?code=' + encodeURIComponent(c.code)
          const go360 = '/supply/partners/' + p.id
          const goDeep = '/supply/codes/' + encodeURIComponent(c.code)
          const modes = deliveryModesOf(c.serviceTypes || [])
          const onb = c.raw && (c.raw.createdDate || c.raw.created_date)
          return (
            <Box
              key={c.code}
              onClick={() => navigate(goDeep)}
              sx={{
                p: 1.75,
                borderRadius: 2.5,
                border: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
                cursor: 'pointer',
                transition: 'all .12s',
                '&:hover': {borderColor: 'primary.light', boxShadow: '0 4px 14px rgba(15,23,42,.06)'},
              }}
            >
              <Stack direction={{xs: 'column', lg: 'row'}} spacing={1.5} alignItems={{lg: 'center'}} justifyContent='space-between'>
                {/* identity */}
                <Box sx={{minWidth: 0, flex: '1 1 200px'}}>
                  <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap>
                    <Typography sx={{fontWeight: 700, fontSize: 13.5, color: 'text.primary'}}>{c.display || c.code}</Typography>
                    <CodeStatusPill status={c.status} />
                  </Stack>
                  <Typography sx={{fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'text.secondary', mt: 0.25}}>{c.code}</Typography>
                </Box>
                {/* service types + modes */}
                <Box sx={{flex: '2 1 300px', minWidth: 0}}>
                  <Stack direction='row' spacing={0.5} flexWrap='wrap' useFlexGap>
                    <ServiceTypeChips codes={c.serviceTypes} max={6} />
                  </Stack>
                  {modes.length ? (
                    <Stack direction='row' spacing={0.5} flexWrap='wrap' useFlexGap sx={{mt: 0.5}}>
                      <ModeChips modes={modes} />
                    </Stack>
                  ) : null}
                </Box>
                {/* agreements + onboarded */}
                <Stack direction='row' spacing={2} alignItems='center' sx={{flexShrink: 0}}>
                  <Box sx={{textAlign: 'center'}}>
                    <Chip size='small' label={agrCnt + ' agr'} sx={{height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: agrCnt ? '#DCFCE7' : '#FEE2E2', color: agrCnt ? '#166534' : '#B91C1C'}} />
                  </Box>
                  <Box sx={{minWidth: 80}}>
                    <Typography sx={{fontSize: 11.5, color: 'text.secondary'}}>{onb ? fmtDate(c.raw.createdDate || c.raw.created_date) : '—'}</Typography>
                    {c.raw && (c.raw.createdByUser || c.raw.created_by_user) ? (
                      <Typography sx={{fontSize: 10.5, color: 'text.disabled'}} noWrap>{c.raw.createdByUser || c.raw.created_by_user}</Typography>
                    ) : null}
                  </Box>
                </Stack>
                {/* actions */}
                <Stack direction='row' spacing={0.5} alignItems='center' sx={{flexShrink: 0}} onClick={(e) => e.stopPropagation()}>
                  <Button size='small' variant='text' onClick={(e) => go(e, goRates)} sx={{textTransform: 'none', fontWeight: 600, fontSize: 12}}>
                    Rates &amp; TAT
                  </Button>
                  <Button size='small' variant='text' onClick={(e) => go(e, go360)} sx={{textTransform: 'none', fontWeight: 600, fontSize: 12}}>
                    360
                  </Button>
                  <Box
                    component='a'
                    href={goDeep}
                    title='Deep dive into this courier code'
                    onClick={(e) => go(e, goDeep)}
                    sx={{display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: '8px', color: 'primary.main', '&:hover': {bgcolor: (th) => alpha(th.palette.primary.main, 0.1)}}}
                  >
                    <VisibilityOutlinedIcon style={ICO} />
                  </Box>
                </Stack>
              </Stack>
            </Box>
          )
        })}
      </Stack>
    </Box>
  )
}

/* ── Parent cell components ──────────────────────────────────────────────── */

/* Partner cell — tinted avatar (p.color) + name link + legal sub. */
function PartnerCell({p, navigate}) {
  const to = '/supply/partners/' + p.id
  return (
    <div className='name-cell' style={{display: 'flex', alignItems: 'center', gap: 10}}>
      <span className='avatar' style={{background: p.color}}>
        {initials(p.name)}
      </span>
      <div>
        <a
          className='lnk'
          href={to}
          onClick={(e) => {
            e.preventDefault()
            navigate(to)
          }}
        >
          {p.name}
        </a>
        <div className='muted' style={{fontSize: 11}}>
          {p.legal}
        </div>
      </div>
    </div>
  )
}

/* Onboarding score — .pbar tinted by scoreColor + % link to the checklist.
   The light list API carries no contacts/codes, so the score is unknown here:
   link straight to the 360 checklist instead. */
function ScoreCell({p, navigate}) {
  const chk = '/supply/partners/' + p.id + '?chk=1'
  if (p.score == null) {
    return (
      <a
        className='lnk'
        style={{fontSize: 12}}
        title='Open the onboarding checklist'
        href={chk}
        onClick={(e) => {
          e.preventDefault()
          navigate(chk)
        }}
      >
        View checklist
      </a>
    )
  }
  const c = scoreColor(p.score)
  return (
    <div className='score-wrap'>
      <span className='pbar'>
        <span style={{width: p.score + '%', background: c}} />
      </span>
      <a
        className='v lnk'
        style={{color: c}}
        title='View onboarding checklist'
        href={chk}
        onClick={(e) => {
          e.preventDefault()
          navigate(chk)
        }}
      >
        {p.score}%
      </a>
    </div>
  )
}

/* Agreements — mini-pills: N signed / N pending (loaded lazily on expand). */
function AgrPills({summary}) {
  if (!summary) {
    return (
      <span className='muted' title='Expand the row to load agreements' style={{fontSize: 11}}>
        —
      </span>
    )
  }
  if (!summary.signed && !summary.pending) {
    return (
      <span className='pillset'>
        <span className='mini-pill mp-red'>No agreement</span>
      </span>
    )
  }
  return (
    <span className='pillset'>
      <span className={cx('mini-pill', summary.signed ? 'mp-green' : 'mp-grey')}>
        {summary.signed} signed
      </span>
      {summary.pending ? <span className='mini-pill mp-amber'>{summary.pending} pending</span> : null}
    </span>
  )
}

/* Status — Active badge, Draft (with a Resume-onboarding link), or Inactive. */
function StatusCell({p}) {
  const navigate = useNavigate()
  if (p.status === 'Active') return <Badge status='active' />
  if (p.status === 'Draft')
    return (
      <span style={{display: 'inline-flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start'}}>
        <span className='badge b-grey'>
          <span className='dot' />
          Draft
        </span>
        <button
          type='button'
          className='lnk'
          style={{fontSize: 11, padding: 0, background: 'none', border: 0, cursor: 'pointer'}}
          title='Finish this courier’s onboarding'
          onClick={(e) => {
            e.stopPropagation()
            navigate('/supply/partners/onboard?resume=' + encodeURIComponent(p.id))
          }}
        >
          Resume onboarding →
        </button>
      </span>
    )
  const tip = p.statusReason ? `Deactivated — ${p.statusReason}` : undefined
  return <Badge status='inactive' title={tip} />
}

export default function PartnerDirectory() {
  const navigate = useNavigate()
  const toast = useToast()
  /* parents list via RTK Query (GET courierPartner/parent/list) */
  const {data: rawParents, isFetching: loading, isError, error: errObj, refetch: loadParents} = useListParentsQuery()
  const [updateParentStatus] = useUpdateParentStatusMutation()
  const [fetchCodes] = useLazyCodesByParentQuery()
  const [fetchTimeline] = useLazyAgreementTimelineQuery()
  const loadError = isError ? (errObj?.message || 'Failed to load partners') : null
  const partners = useMemo(() => (rawParents || []).map(mapParent), [rawParents])
  // per-parent lazy expand cache: {pid: {loading, codes, timeline, error}}
  const [expandData, setExpandData] = useState({})
  const [confirm, setConfirm] = useState(null) // {partner, deact}
  const [bulkConfirm, setBulkConfirm] = useState(null) // {rows, clear}
  const [dateRange, setDateRange] = useState({preset: 'all', from: '', to: ''})
  const [statusFilter, setStatusFilter] = useState('All')

  const counts = useMemo(() => {
    const c = {total: partners.length, Active: 0, Draft: 0, Inactive: 0}
    partners.forEach((p) => {
      c[p.status] = (c[p.status] || 0) + 1
    })
    return c
  }, [partners])

  /* lazy per-parent fetch — codes + agreement timeline, on first row expand */
  const loadExpand = useCallback((pid) => {
    setExpandData((m) => {
      if (m[pid]) return m // already loading / loaded
      Promise.all([fetchCodes(pid).unwrap(), fetchTimeline(pid).unwrap()])
        .then(([codesRes, tlRes]) => {
          const docs = Array.isArray(codesRes) ? codesRes : []
          const timeline = tlRes || []
          setExpandData((cur) => ({
            ...cur,
            [pid]: {loading: false, codes: docs.map(mapCode), timeline},
          }))
        })
        .catch((e) =>
          setExpandData((cur) => ({...cur, [pid]: {loading: false, error: apiError(e)}}))
        )
      return {...m, [pid]: {loading: true}}
    })
  }, [fetchCodes, fetchTimeline])

  /* togglePartner(): both directions confirmed; deactivate needs reason.
     The mutation invalidates the Parent LIST + Dashboard tags, so the list
     auto-refetches — no explicit reload needed. */
  const doToggle = (p, reason) => {
    const deact = p.status === 'Active'
    updateParentStatus({id: p.id, status: deact ? 'INACTIVE' : 'ACTIVE', reason: reason || undefined})
      .unwrap()
      .then(() => {
        toast(p.name + (deact ? ' deactivated' : ' activated') + '. Audit recorded.', 'success')
      })
      .catch((e) => toast(e?.message || apiError(e), 'error'))
  }

  /* bulk status change — parallel mutations, toast per failure + summary */
  const bulkStatus = (rows, status, reason, clear) => {
    Promise.all(
      rows.map((p) =>
        updateParentStatus({id: p.id, status, reason: reason || undefined})
          .unwrap()
          .then(() => ({ok: true}))
          .catch((e) => ({ok: false, name: p.name, msg: e?.message || 'Failed'}))
      )
    ).then((results) => {
      const ok = results.filter((r) => r.ok).length
      results
        .filter((r) => !r.ok)
        .forEach((r) => toast(`${r.name}: ${r.msg}`, 'error'))
      if (ok)
        toast(`${ok} partner(s) ${status === 'ACTIVE' ? 'activated' : 'deactivated'}.`, 'success')
      clear()
    })
  }

  /* dateColumn:'updated' — pre-filter rows by the date-range chip */
  const rowsFn = () => {
    let rows = partners.slice()
    if (statusFilter !== 'All') rows = rows.filter((p) => p.status === statusFilter)
    if (dateRange.preset !== 'all') {
      const rng = presetRange(dateRange.preset, dateRange.from, dateRange.to)
      if (rng)
        rows = rows.filter((p) => {
          if (!p.updated) return false
          const d = new Date(p.updated)
          return (!rng.lo || d >= rng.lo) && (!rng.hi || d <= rng.hi)
        })
    }
    return rows
  }

  const columns = [
    {
      key: 'name',
      label: 'Parent Courier Partner',
      render: (p) => <PartnerCell p={p} navigate={navigate} />,
      value: (p) => p.name,
    },
    {
      key: 'd2cTotal',
      label: '# Account Codes',
      align: 'center',
      sortable: false,
      render: (p) => {
        const e = expandData[p.id]
        return e && e.codes ? (
          <span style={{fontWeight: 600}}>{e.codes.length}</span>
        ) : (
          <span className='muted' title='Expand the row to load codes'>—</span>
        )
      },
      exportValue: (p) => {
        const e = expandData[p.id]
        return e && e.codes ? e.codes.length : ''
      },
    },
    {
      key: 'd2cActive',
      label: 'Active',
      align: 'center',
      sortable: false,
      render: (p) => {
        const e = expandData[p.id]
        if (!e || !e.codes) return <span className='muted'>—</span>
        const active = e.codes.filter((c) => c.status === 'Active').length
        const total = e.codes.length
        const color = active === 0 ? 'var(--error,#C62828)' : active < total ? '#B45309' : '#2E7D32'
        return (
          <>
            <span style={{fontWeight: 700, color}}>{active}</span>
            <span className='muted' style={{fontSize: 11}}> / {total}</span>
          </>
        )
      },
      exportValue: (p) => {
        const e = expandData[p.id]
        return e && e.codes ? e.codes.filter((c) => c.status === 'Active').length : ''
      },
    },
    {
      key: 'deliveryModes',
      label: 'Delivery Modes',
      sortable: false,
      render: (p) => {
        const e = expandData[p.id]
        if (!e || !e.codes) return <span className='muted' style={{fontSize: 11}}>—</span>
        return <ModeChips modes={modesForCodes(e.codes)} />
      },
      exportValue: (p) => {
        const e = expandData[p.id]
        return e && e.codes ? modesForCodes(e.codes).join('; ') : ''
      },
    },
    {
      key: 'agr',
      label: 'Agreements',
      sortable: false,
      render: (p) => {
        const e = expandData[p.id]
        return <AgrPills summary={e && e.timeline ? timelineSummary(e.timeline) : null} />
      },
      exportValue: (p) => {
        const e = expandData[p.id]
        if (!e || !e.timeline) return ''
        const s = timelineSummary(e.timeline)
        return s.signed + ' signed / ' + s.pending + ' pending'
      },
    },
    {
      key: 'score',
      label: 'Onboarding',
      sortable: false,
      render: (p) => <ScoreCell p={p} navigate={navigate} />,
      exportValue: () => '',
    },
    {
      key: 'status',
      label: 'Status',
      render: (p) => <StatusCell p={p} />,
      exportValue: (p) => p.status,
    },
    {
      key: 'updated',
      label: 'Last Updated',
      render: (p) => <span title={p.updated ? fmtDate(p.updated) : undefined}>{relNow(p.updated)}</span>,
      value: (p) => p.updated,
      exportValue: (p) => p.updated,
    },
  ]

  const rowActions = (p) => [
    {
      label: 'View 360',
      icon: <VisibilityOutlinedIcon fontSize='small' />,
      onClick: () => navigate('/supply/partners/' + p.id),
    },
    {
      label: 'Edit details',
      icon: <EditOutlinedIcon fontSize='small' />,
      onClick: () => navigate('/supply/partners/' + p.id),
    },
    {sep: true},
    {
      label: p.status === 'Active' ? 'Deactivate' : 'Activate',
      icon: <PowerSettingsNewOutlinedIcon fontSize='small' />,
      danger: p.status === 'Active',
      onClick: () => setConfirm({partner: p, deact: p.status === 'Active'}),
    },
  ]

  const bulkActions = [
    {
      label: 'Activate',
      icon: <PowerSettingsNewOutlinedIcon fontSize='small' />,
      confirm: false,
      onClick: (rows, clear) => bulkStatus(rows, 'ACTIVE', undefined, clear),
    },
    {
      label: 'Deactivate',
      icon: <PowerSettingsNewOutlinedIcon fontSize='small' />,
      danger: true,
      confirm: false,
      onClick: (rows, clear) => setBulkConfirm({rows, clear}),
    },
    {
      label: 'Export selected',
      icon: <DownloadOutlinedIcon fontSize='small' />,
      confirm: false,
      onClick: (rows, clear) => {
        download(
          'courier-partners-d2c-selected_' + new Date().toISOString().slice(0, 10) + '.csv',
          toCsv(rows, [
            {label: 'Partner', value: (p) => p.name},
            {label: 'Legal', value: (p) => p.legal},
            {
              label: 'Codes',
              value: (p) => {
                const e = expandData[p.id]
                return e && e.codes ? e.codes.length : ''
              },
            },
            {
              label: 'Active',
              value: (p) => {
                const e = expandData[p.id]
                return e && e.codes ? e.codes.filter((c) => c.status === 'Active').length : ''
              },
            },
            {label: 'Status', value: (p) => p.status},
          ])
        )
        toast(`Exported ${rows.length} selected.`, 'success')
        clear()
      },
    },
  ]

  return (
    <>
      <PageTitle>{'Courier Partner Directory'}</PageTitle>

      <SupplyHero
        icon={<StorefrontOutlinedIcon />}
        title='Courier Partner Directory'
        subtitle='D2C courier partners — expand a row for account codes, or open a partner’s 360 view.'
        actions={
          <>
            <Button variant='outlined' startIcon={<SyncOutlinedIcon />} onClick={() => navigate('/supply/partners/update')} sx={HERO_BTN}>
              Update Rates &amp; TAT
            </Button>
            <Button variant='contained' disableElevation startIcon={<AddCircleOutlineIcon />} onClick={() => navigate('/supply/partners/onboard')} sx={HERO_BTN_SOLID}>
              Onboard Partner
            </Button>
          </>
        }
      />

      {/* stat cards — also the front-facing status filter (click to filter) */}
      <Box sx={{display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2}}>
        {[
          {label: 'Total Partners', value: counts.total, icon: <StorefrontOutlinedIcon />, accent: '#2065D1', s: 'All'},
          {label: 'Active', value: counts.Active || 0, icon: <CheckCircleOutlinedIcon />, accent: '#16A34A', s: 'Active'},
          {label: 'Draft', value: counts.Draft || 0, icon: <PendingActionsOutlinedIcon />, accent: '#D97706', s: 'Draft'},
          {label: 'Inactive', value: counts.Inactive || 0, icon: <BlockOutlinedIcon />, accent: '#DC2626', s: 'Inactive'},
        ].map((k) => {
          const active = statusFilter === k.s
          return (
            <Box
              key={k.label}
              onClick={() => setStatusFilter(active && k.s !== 'All' ? 'All' : k.s)}
              sx={{
                flex: '1 1 170px',
                minWidth: 0,
                p: 2,
                borderRadius: 3,
                border: 2,
                borderColor: active ? k.accent : 'divider',
                bgcolor: active ? alpha(k.accent, 0.04) : 'background.paper',
                cursor: 'pointer',
                transition: 'all .14s',
                '&:hover': {transform: 'translateY(-2px)', boxShadow: '0 8px 22px rgba(15,23,42,.08)', borderColor: active ? k.accent : alpha(k.accent, 0.4)},
              }}
            >
              <Stack direction='row' justifyContent='space-between' alignItems='flex-start' spacing={1}>
                <Box sx={{minWidth: 0}}>
                  <Typography sx={{fontSize: 26, fontWeight: 800, letterSpacing: '-.5px', lineHeight: 1, color: 'text.primary'}}>{k.value}</Typography>
                  <Typography sx={{fontSize: 12, fontWeight: 600, color: 'text.secondary', mt: 0.9}}>{k.label}</Typography>
                </Box>
                <Box sx={{width: 36, height: 36, borderRadius: '11px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(k.accent, 0.12), color: k.accent}}>{k.icon}</Box>
              </Stack>
            </Box>
          )
        })}
      </Box>

      {/* inline filter row — front-facing, no side drawer */}
      <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap sx={{mb: 1.5}}>
        <Typography sx={{fontSize: 12, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: 'text.secondary', mr: 0.5}}>Filter</Typography>
        {['All', 'Active', 'Draft', 'Inactive'].map((s) => {
          const active = statusFilter === s
          return (
            <Chip
              key={s}
              label={s}
              onClick={() => setStatusFilter(s)}
              sx={{height: 30, fontWeight: 600, fontSize: 12.5, cursor: 'pointer', border: 1, borderColor: active ? 'primary.main' : 'divider', bgcolor: active ? 'primary.main' : 'background.paper', color: active ? '#fff' : 'text.secondary', '&:hover': {bgcolor: active ? 'primary.dark' : 'action.hover'}}}
            />
          )
        })}
        <Box sx={{flexGrow: 1}} />
        <DateRangeChip range={dateRange} onChange={setDateRange} />
        {(statusFilter !== 'All' || dateRange.preset !== 'all') ? (
          <Button size='small' onClick={() => { setStatusFilter('All'); setDateRange({preset: 'all', from: '', to: ''}) }} sx={{textTransform: 'none', fontWeight: 600}}>
            Clear
          </Button>
        ) : null}
      </Stack>

      {loading ? (
        <TableSkeleton rows={8} />
      ) : loadError ? (
        <LoadErrorPanel message={loadError} onRetry={loadParents} title='Couldn’t load partners' />
      ) : (
        <SupplyTable
          columns={columns}
          rows={rowsFn}
          rowKey={(p) => p.id}
          searchKeys={['name', 'legal']}
          searchPlaceholder='Search by partner or legal name…'
          defaultSort={{key: 'name', dir: 'asc'}}
          rowClick={(p) => navigate('/supply/partners/' + p.id)}
          rowActions={rowActions}
          bulkActions={bulkActions}
          expand={(p) => (
            <CodeSubTable p={p} entry={expandData[p.id]} navigate={navigate} onExpand={loadExpand} />
          )}
          exportName='courier-partners-d2c'
          emptyText='No courier partners onboarded yet.'
          emptyCta={{
            label: 'Onboard Partner',
            icon: <AddCircleOutlineIcon style={ICO} />,
            onClick: () => navigate('/supply/partners/onboard'),
          }}
        />
      )}

      {/* single-partner activate / deactivate (mockup togglePartner) */}
      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        title={
          confirm
            ? `${confirm.deact ? 'Deactivate' : 'Activate'} ${confirm.partner.name}?`
            : ''
        }
        body={
          confirm
            ? confirm.deact
              ? `This will immediately remove ${confirm.partner.name} from PACE allocation.`
              : `Restore ${confirm.partner.name} and its codes to allocation eligibility.`
            : ''
        }
        bullets={
          confirm && confirm.deact
            ? [
                'Remove from PACE allocation',
                'Flag all active shipments on this partner’s codes for manual review',
                'Codes remain in their current state until individually managed',
              ]
            : undefined
        }
        needReason={!!(confirm && confirm.deact)}
        danger={!!(confirm && confirm.deact)}
        okLabel={confirm && !confirm.deact ? 'Activate' : 'Deactivate'}
        onOk={(reason) => {
          if (confirm) doToggle(confirm.partner, reason)
          setConfirm(null)
        }}
      />

      {/* bulk deactivate (reason capture, mockup bulkActions) */}
      <ConfirmDialog
        open={!!bulkConfirm}
        onClose={() => setBulkConfirm(null)}
        title={bulkConfirm ? `Deactivate ${bulkConfirm.rows.length} partner(s)?` : ''}
        bullets={[
          'Remove from PACE allocation',
          'Flag active shipments on their codes for manual review',
        ]}
        needReason
        danger
        okLabel='Deactivate'
        onOk={(reason) => {
          if (bulkConfirm) bulkStatus(bulkConfirm.rows, 'INACTIVE', reason, bulkConfirm.clear)
          setBulkConfirm(null)
        }}
      />
    </>
  )
}
